import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  ShareNetworkIcon,
  PlusCircleIcon,
  DeviceMobileIcon,
  BrowserIcon,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import {
  canPromptForPwaInstall,
  initializePwaInstall,
  promptForPwaInstall,
} from "@/lib/pwaInstall";

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isInstalled = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export default function InstallApp() {
  const [promptAvailable, setPromptAvailable] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  const refreshState = useCallback(() => {
    setPromptAvailable(canPromptForPwaInstall());
    setInstalled(isInstalled());
  }, []);

  useEffect(() => {
    initializePwaInstall();
    refreshState();

    window.addEventListener("bikeparks-install-state-change", refreshState);
    window.addEventListener("focus", refreshState);
    return () => {
      window.removeEventListener("bikeparks-install-state-change", refreshState);
      window.removeEventListener("focus", refreshState);
    };
  }, [refreshState]);

  const ios = useMemo(() => isIos(), []);

  const install = async () => {
    setInstalling(true);
    await promptForPwaInstall();
    refreshState();
    setInstalling(false);
  };

  return (
    <main className="min-h-screen bg-[#09090b] text-white">
      <Navbar />
      <section className="mx-auto flex min-h-screen max-w-5xl items-center px-4 pb-12 pt-24 sm:px-6 md:pt-28">
        <div className="grid w-full overflow-hidden rounded-3xl border border-white/10 bg-[#111214] shadow-2xl md:grid-cols-[1.08fr_.92fr]">
          <div className="p-7 sm:p-10">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-400 transition-colors hover:text-[#CCFF00]">
              <ArrowLeftIcon size={17} /> {translateLabel("Back to Bikeparks Finder")}
            </Link>

            <div className="mt-10">
              <div className="mb-5 inline-flex rounded-2xl border border-white/10 bg-black/40 p-3">
                <img src="/icons/app-icon-512.png" alt="" className="h-14 w-14 rounded-xl" />
              </div>
              <p className="label-tiny text-[#CCFF00]">{translateLabel("Bikeparks Finder app")}</p>
              <h1 className="mt-2 font-display text-5xl font-extrabold uppercase leading-[.88] sm:text-6xl">
                {translateLabel("Keep your next ride one tap away.")}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-7 text-zinc-300">
                {translateLabel("Install Bikeparks Finder on your phone or computer for a clean full-screen experience and quick access before every ride.")}
              </p>
            </div>

            {installed ? (
              <div className="mt-8 flex items-center gap-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-emerald-200">
                <CheckCircleIcon size={25} weight="fill" className="shrink-0 text-emerald-400" />
                <span className="font-semibold">{translateLabel("Bikeparks Finder is already installed on this device.")}</span>
              </div>
            ) : promptAvailable ? (
              <button
                type="button"
                onClick={install}
                disabled={installing}
                className="mt-8 inline-flex min-h-14 items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-6 text-base font-extrabold text-black transition-transform hover:scale-[1.02] disabled:cursor-wait disabled:opacity-70"
              >
                <DownloadSimpleIcon size={22} weight="bold" />
                {translateLabel(installing ? "Opening install…" : "Install Bikeparks Finder")}
              </button>
            ) : ios ? (
              <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-5">
                <p className="font-semibold text-white">{translateLabel("Install it from Safari")}</p>
                <p className="mt-1 text-sm leading-6 text-zinc-400">{translateLabel("Apple requires this final step in Safari.")}</p>
                <ol className="mt-5 space-y-4 text-sm text-zinc-200">
                  <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 font-bold text-[#CCFF00]">1</span><span><ShareNetworkIcon className="mr-1 inline text-[#CCFF00]" size={18} weight="bold" /> {translateLabel("Tap")} <strong>{translateLabel("Share")}</strong> {translateLabel("in Safari.")}</span></li>
                  <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 font-bold text-[#CCFF00]">2</span><span><PlusCircleIcon className="mr-1 inline text-[#CCFF00]" size={18} weight="bold" /> {translateLabel("Choose")} <strong>{translateLabel("Add to Home Screen")}</strong>.</span></li>
                  <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 font-bold text-[#CCFF00]">3</span><span>{translateLabel("Tap")} <strong>{translateLabel("Add")}</strong> {translateLabel("— then open it like a normal app.")}</span></li>
                </ol>
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-5">
                <p className="font-semibold text-white">{translateLabel("Install from your browser menu")}</p>
                <p className="mt-1 text-sm leading-6 text-zinc-400">
                  {translateLabel("In Chrome or Edge, open the browser menu and choose Install app. If you just opened the site, browse for a moment and then return here.")}
                </p>
              </div>
            )}
          </div>

          <aside className="flex flex-col justify-between border-t border-white/10 bg-[radial-gradient(circle_at_50%_18%,rgba(204,255,0,.14),transparent_36%),linear-gradient(145deg,#17231f,#090b0a)] p-7 sm:p-10 md:border-l md:border-t-0">
            <div>
              <DeviceMobileIcon size={34} weight="duotone" className="text-[#CCFF00]" />
              <h2 className="mt-6 font-display text-3xl font-extrabold uppercase">{translateLabel("Built for ride day")}</h2>
              <ul className="mt-6 space-y-4 text-sm leading-6 text-zinc-300">
                <li className="flex gap-3"><CheckCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-[#CCFF00]" /> {translateLabel("Opens from your home screen or desktop without browser clutter.")}</li>
                <li className="flex gap-3"><CheckCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-[#CCFF00]" /> {translateLabel("The app shell stays available after you have opened it once.")}</li>
                <li className="flex gap-3"><CheckCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-[#CCFF00]" /> {translateLabel("Park information and maps still refresh online, so the data stays current.")}</li>
              </ul>
            </div>
            <div className="mt-10 rounded-2xl border border-white/10 bg-black/25 p-4 text-sm text-zinc-400">
              <BrowserIcon className="mb-2 text-[#CCFF00]" size={20} weight="duotone" />
              {translateLabel("Best experience: Safari on iPhone, Chrome on Android, or Chrome/Edge on desktop.")}
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
